import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Home, Tag, ImageIcon, ScrollText, LayoutGrid,
} from 'lucide-react';

/**
 * Fixed bottom navigation bar for mobile viewports (< 640px).
 *
 * Feb 2026 (user "go back to the original 5 bottom bars and not swipable
 * for more, make a more section going down like it was before"):
 *   • Back to the ORIGINAL five fixed tabs — no horizontal scroll, no
 *     fade-hint chevrons, no snap-scroll math. Just five buttons that
 *     each take exactly 20% of the viewport width.
 *   • Feb 2026 (user follow-up "there should not be a video bottom bar
 *     as already in picture library"): Videos slot removed — Videos is
 *     already one tap away from Pictures via the MediaLibraryTabs
 *     switcher, so a top-level bottom tab is redundant. Transcripts
 *     takes that slot instead.
 *   • Fifth tab is the "More" bucket → routes to /more, which is the
 *     familiar scroll-down list (Browse · Your Library · The Vaad ·
 *     Preferences · Legal · Admin).
 *   • Height is a shared CSS variable (`--vaad-mobile-nav-h`) so the
 *     MiniPlayer sits pixel-perfect on top with zero gap or overlap.
 */

const MobileBottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const items = [
    { id: 'home',        Icon: Home,       label: 'Home',        path: '/' },
    { id: 'pictures',    Icon: ImageIcon,  label: 'Pictures',    path: '/cc-picture' },
    { id: 'topics',      Icon: Tag,        label: 'By Topic',    path: '/topics' },
    { id: 'transcripts', Icon: ScrollText, label: 'Transcripts', path: '/transcripts' },
    { id: 'more',        Icon: LayoutGrid, label: 'More',        path: '/more' },
  ];

  const isActive = (item) => {
    if (item.id === 'home') return location.pathname === '/';
    if (item.id === 'topics') {
      return location.pathname.startsWith('/topics')
        || location.pathname.startsWith('/topic')
        || location.pathname.startsWith('/parsha');
    }
    const map = {
      pictures: '/cc-picture',
      transcripts: '/transcripts',
      more: '/more',
    };
    const prefix = map[item.id];
    return prefix ? location.pathname.startsWith(prefix) : false;
  };

  return (
    <nav
      className="sm:hidden fixed bottom-0 left-0 right-0 z-30"
      data-testid="mobile-bottom-nav"
      style={{
        // Shared height variable so the MiniPlayer can dock right on
        // top with zero pixel gap. Total = pad-top (6) + button (52)
        // + pad-bot (6) = 64px, then + iOS safe-area.
        height: 'calc(var(--vaad-mobile-nav-h, 64px) + env(safe-area-inset-bottom, 0px))',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        // Feb 2026 (user "the audio player that follows you must match
        // the bottom bar perfectly"): gradient starts where the
        // MiniPlayer's gradient ends (0.72 alpha) so when they stack,
        // the total surface reads as one continuous vertical gradient
        // from 0.55 (top of mini-player) → 0.90 (bottom of nav).
        background: 'linear-gradient(180deg, rgba(4,42,61,0.72) 0%, rgba(4,42,61,0.90) 60%)',
        WebkitBackdropFilter: 'blur(26px) saturate(220%) contrast(110%)',
        backdropFilter: 'blur(26px) saturate(220%) contrast(110%)',
        borderTop: '1px solid rgba(230,200,100,0.40)',
        boxShadow: '0 -6px 20px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.08)',
      }}
    >
      <div className="flex w-full h-full" data-testid="mobile-bottom-nav-inner">
        {items.map((item) => {
          const Icon = item.Icon;
          const active = isActive(item);
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => navigate(item.path)}
              className={`vaad-nav-pill flex-1 flex flex-col items-center justify-center gap-0.5 mx-0.5 my-1 rounded-2xl transition-all duration-300 ${active ? 'vaad-nav-pill--active' : ''}`}
              data-testid={`mobile-nav-${item.id}`}
              aria-current={active ? 'page' : undefined}
            >
              <Icon className={`w-[22px] h-[22px] transition-transform duration-300 ${active ? 'scale-110' : ''}`} />
              <span className="text-[10px] font-semibold leading-none tracking-wide">{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileBottomNav;
